import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";


export interface IUserSubscription {
  created_at: string;
  is_onboarded: boolean;
  plan: string | null;
  price: number | null;
  promo_applied: boolean;
  status: string;
  stripe_customer_id: string | null;
  updated_at: string;
  user_id: string;
  username: string | null;
  user_rating?: { score: number }
	avatar?: string;
}

interface ISubscriptionContextType {
  subscription: IUserSubscription | null;
  isLoading: boolean;
  error: Error | null;
  isTrial: boolean;
  isOnboarded: boolean;
}


const initialState: ISubscriptionContextType = {
  subscription: null,
  isLoading: false,
  error: null,
  isTrial: true,
  isOnboarded: false,
};

const SubscriptionContext = createContext<ISubscriptionContextType>(initialState);


interface SubscriptionProviderProps {
  children: ReactNode;
}

function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const user = useUser();

  const {
    data: subscription,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["user-subscription", user?.id],
    queryFn: async (): Promise<IUserSubscription | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("user_subscriptions")
        .select("*, user_rating(score)")
        .eq("user_id", user.id)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return null;
        }
        throw error;
      }
      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const isTrial = subscription?.status !== "active";
  const isOnboarded = subscription?.is_onboarded ?? false;

  const contextValue: ISubscriptionContextType = {
    subscription,
    isLoading,
    error: error as Error | null,
    isTrial,
    isOnboarded,
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}

function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription was used outside the SubscriptionProvider"
    );
  }
  return context;
}

export { SubscriptionProvider, useSubscription };
